import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DEFAULT_FILTERS, useItemStats } from "../hooks/useInventory.js";
import { api, downloadCsv } from "../lib/api.js";
import { formatCurrency } from "../lib/format.js";
import { Button, Card, ErrorBanner, Spinner } from "../components/ui.js";

type WhatnotReportType = "seller_financial_report" | "analytics_overview_report" | "analytics_livestreams_report";
interface WhatnotReportRun {
  id: string;
  reportType: WhatnotReportType;
  status: string;
  parameters: Record<string, unknown>;
  requestedAt: string;
  completedAt: string | null;
  importedAt: string | null;
  rowCount: number;
  error: string | null;
}

const REPORT_LABELS: Record<WhatnotReportType, string> = {
  seller_financial_report: "Financial transactions",
  analytics_overview_report: "Overview analytics",
  analytics_livestreams_report: "Livestream analytics",
};

function useWhatnotReports() {
  return useQuery({
    queryKey: ["whatnot", "reports"],
    queryFn: () => api.get<{ data: WhatnotReportRun[] }>("/whatnot/reports"),
    staleTime: 30_000,
  });
}

function ReportMetric({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return (
    <Card className="min-w-0">
      <div className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-2 text-2xl font-semibold text-slate-100">{value}</div>
      {detail ? <p className="mt-1 text-xs text-slate-400">{detail}</p> : null}
    </Card>
  );
}

/** Aggregate inventory reporting, separate from day-to-day inventory work. */
export function ReportsPage() {
  const stats = useItemStats({ ...DEFAULT_FILTERS, status: "all" });
  const queryClient = useQueryClient();
  const reportRuns = useWhatnotReports();
  const status = useQuery({
    queryKey: ["whatnot", "status"],
    queryFn: () => api.get<{ data: { configured: boolean } }>("/whatnot/status"),
    staleTime: 60_000,
  });
  const [reportType, setReportType] = useState<WhatnotReportType>("seller_financial_report");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [expandedRun, setExpandedRun] = useState<string | null>(null);
  const [rowData, setRowData] = useState<Record<string, unknown>[]>([]);
  const dateRangeError = useMemo(() => startDate && endDate && startDate >= endDate, [startDate, endDate]);
  const missingOverviewRange = reportType === "analytics_overview_report" && (!startDate || !endDate);

  const createReport = useMutation({
    mutationFn: () => api.post<{ data: WhatnotReportRun }>("/whatnot/reports", {
      reportType,
      ...(reportType !== "analytics_livestreams_report" && startDate ? { startDate: new Date(`${startDate}T00:00:00.000Z`).toISOString() } : {}),
      ...(reportType !== "analytics_livestreams_report" && endDate ? { endDate: new Date(`${endDate}T23:59:59.999Z`).toISOString() } : {}),
    }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["whatnot", "reports"] }),
  });
  const refreshReport = useMutation({
    mutationFn: (id: string) => api.post<{ data: WhatnotReportRun }>(`/whatnot/reports/${id}/refresh`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["whatnot", "reports"] }),
  });

  async function showRows(runId: string) {
    if (expandedRun === runId) {
      setExpandedRun(null);
      return;
    }
    const result = await api.get<{ data: Record<string, unknown>[] }>(`/whatnot/reports/${runId}/rows`);
    setRowData(result.data);
    setExpandedRun(runId);
  }

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-lg font-semibold text-slate-100">Reports</h1>
        <p className="mt-1 text-sm text-slate-400">
          A summary of your active inventory, including items marked sold.
        </p>
      </header>

      {stats.isError ? <ErrorBanner message={(stats.error as Error).message} /> : null}
      {stats.isLoading ? (
        <Spinner label="Loading inventory report…" />
      ) : stats.data ? (
        <>
          <section aria-label="Inventory summary" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <ReportMetric label="Total items" value={String(stats.data.total)} />
            <ReportMetric label="Available" value={String(stats.data.available)} />
            <ReportMetric label="Sold" value={String(stats.data.sold)} />
            <ReportMetric
              label="Purchase cost"
              value={formatCurrency(stats.data.totalCost)}
              detail="Sum of recorded purchase prices"
            />
            <ReportMetric
              label="Estimated value"
              value={formatCurrency(stats.data.estimatedValue)}
              detail="Sum of current item values"
            />
          </section>
          <p className="text-xs text-slate-400">
            Deleted items in the recycle bin are excluded. Currency totals use the values currently recorded on each item.
          </p>
        </>
      ) : null}

      <section aria-label="Whatnot reports" className="space-y-3">
        <header>
          <h2 className="text-base font-semibold text-slate-100">Whatnot reports</h2>
          <p className="mt-1 text-sm text-slate-400">
            Request fresh reports when needed. Completed results are stored in Farnsworth and reused when you return to this page.
          </p>
        </header>
        {status.data && !status.data.data.configured ? (
          <Card><p className="text-sm text-amber-200">Whatnot is not connected. Configure WHATNOT_API_TOKEN on the server to request reports.</p></Card>
        ) : null}
        {reportRuns.isError ? <ErrorBanner message={(reportRuns.error as Error).message} /> : null}
        {createReport.isError ? <ErrorBanner message={(createReport.error as Error).message} /> : null}
        {refreshReport.isError ? <ErrorBanner message={(refreshReport.error as Error).message} /> : null}
        <Card className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:items-end">
            <label className="block text-sm text-slate-300">
              Report type
              <select value={reportType} onChange={(event) => setReportType(event.target.value as WhatnotReportType)} className="mt-1 block min-h-10 w-full rounded-md border border-slate-700 bg-slate-900 px-3 text-slate-100">
                {Object.entries(REPORT_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
            {reportType !== "analytics_livestreams_report" ? <>
              <label className="block text-sm text-slate-300">
                Start date
                <input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} className="mt-1 block min-h-10 w-full rounded-md border border-slate-700 bg-slate-900 px-3 text-slate-100" />
              </label>
              <label className="block text-sm text-slate-300">
                End date
                <input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} className="mt-1 block min-h-10 w-full rounded-md border border-slate-700 bg-slate-900 px-3 text-slate-100" />
              </label>
            </> : <p className="text-sm text-slate-400">This report includes all matching shows.</p>}
            <Button type="button" disabled={!status.data?.data.configured || createReport.isPending || Boolean(dateRangeError) || missingOverviewRange} onClick={() => createReport.mutate()}>
              {createReport.isPending ? "Requesting…" : "Request report"}
            </Button>
          </div>
          {dateRangeError ? <p className="text-sm text-rose-300">Start date must be earlier than end date.</p> : null}
          {missingOverviewRange ? <p className="text-sm text-rose-300">Overview analytics needs both dates.</p> : null}
          <p className="text-xs text-slate-400">Date filters are optional. Report data may contain Whatnot estimates; financial rows are transaction based.</p>
        </Card>

        {reportRuns.isLoading ? <Spinner label="Loading saved Whatnot reports…" /> : null}
        <div className="space-y-2">
          {reportRuns.data?.data.map((run) => (
            <Card key={run.id} className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="font-medium text-slate-100">{REPORT_LABELS[run.reportType] ?? run.reportType}</div>
                  <div className="mt-1 text-xs text-slate-400">
                    Requested {new Date(run.requestedAt).toLocaleString()} · {run.rowCount} rows · {run.status.replaceAll("_", " ")}
                    {run.importedAt ? ` · cached ${new Date(run.importedAt).toLocaleString()}` : ""}
                  </div>
                  {run.error ? <div className="mt-1 text-xs text-rose-300">{run.error}</div> : null}
                </div>
                <div className="flex gap-2">
                  {run.status === "pending" || run.status === "in_progress" || (run.status === "completed" && !run.importedAt) ? (
                    <Button type="button" variant="secondary" disabled={refreshReport.isPending} onClick={() => refreshReport.mutate(run.id)}>
                      {refreshReport.isPending && refreshReport.variables === run.id ? "Checking…" : "Check for results"}
                    </Button>
                  ) : null}
                  {run.importedAt ? <>
                    <Button type="button" variant="ghost" onClick={() => void downloadCsv(`/whatnot/reports/${run.id}/download`, `whatnot-${run.reportType}.csv`)}>Download CSV</Button>
                    <Button type="button" variant="ghost" onClick={() => void showRows(run.id)}>{expandedRun === run.id ? "Hide rows" : "View rows"}</Button>
                  </> : null}
                </div>
              </div>
              {expandedRun === run.id ? <ReportRows rows={rowData} /> : null}
            </Card>
          ))}
          {!reportRuns.isLoading && !reportRuns.data?.data.length ? <Card><p className="text-sm text-slate-400">No Whatnot reports requested yet.</p></Card> : null}
        </div>
      </section>
    </div>
  );
}

function ReportRows({ rows }: { rows: Record<string, unknown>[] }) {
  if (!rows.length) return <p className="text-sm text-slate-400">This report has no imported rows.</p>;
  const headers = Object.keys(rows[0] ?? {});
  return (
    <div className="overflow-x-auto rounded-md border border-slate-800">
      <table className="min-w-full text-left text-xs">
        <thead className="bg-slate-900 text-slate-300"><tr>{headers.map((header) => <th key={header} className="whitespace-nowrap px-3 py-2 font-medium">{header.replaceAll("_", " ")}</th>)}</tr></thead>
        <tbody className="divide-y divide-slate-800">{rows.slice(0, 30).map((row, index) => <tr key={index} className="text-slate-300">{headers.map((header) => <td key={header} className="max-w-64 truncate whitespace-nowrap px-3 py-2">{String(row[header] ?? "")}</td>)}</tr>)}</tbody>
      </table>
      {rows.length > 30 ? <p className="p-2 text-xs text-slate-400">Showing first 30 of {rows.length} rows.</p> : null}
    </div>
  );
}
